import {
    Action,
    ActionReducer,
    ActionReducerMap,
    createAction,
    createFeatureSelector,
    createSelector,
    MetaReducer,
    props
} from '@ngrx/store';
import { ActivatedRouteSnapshot, Params, RouterStateSnapshot } from '@angular/router';
import { routerReducer, RouterReducerState, RouterStateSerializer } from '@ngrx/router-store';

import { environment } from '../../environments/environment'; // Angular CLI environment
import * as fromCart from './cart/cart.reducers';
import * as fromLayout from './layout/layout.reducers';
import * as layout from './layout/layout.reducers';
import * as samplingSupplies from './sampling-supplies/sampling-supplies.reducers';
import * as fromContact from './labsys-contact/labsys-contact.reducers';
import * as fromAuth from './auth/auth.reducers';
import * as fromPayments from './payments/payments.reducer';
import { localStorageSync } from 'ngrx-store-localstorage';
import { Type, Injectable } from '@angular/core';

export interface AppState {
    cart: fromCart.State;
    layout: layout.State;
    samplingSupplies: samplingSupplies.State;
    contact: fromContact.State;
    auth: fromAuth.State;
    payments: fromPayments.PaymentsState;
    router: RouterReducerState;
}

export function debug(reducer: ActionReducer<any>): ActionReducer<any> {
    return function (state, action) {
    // console.debug('state', state);
    // console.debug('action', action);
        return reducer(state, action);
    };
}

/**
 * exclude Cart state from deep freezing because this will take overwhelming effort and will break
 * existing code which are mutating the state.
 */
export function freezer(reducer: ActionReducer<any>): ActionReducer<any> {
    const exclusions: RegExp[] = [
        /^ADD_CART_ITEM/,
        /^UPDATE_CART_ITEM/,
        /^UPDATE_CART/,
        /^PLACE_ORDER/,
        /^PLACE_ORDER_SUCCESS/,
        /^PLACE_ORDER_FAILURE/
    ];

    return function (state: any, action: Action): any {
        const excluded = exclusions.reduce((current, next) => {
            const isExcluded = Boolean(action.type.match(next));

            return current || isExcluded;
        }, false);

        if (excluded) {
            return reducer(state, action);
        }

    // return storeFreeze(reducer)(state, action);
    };
}

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
    return localStorageSync({
        rehydrate: true,
        removeOnUndefined: true,
        keys: [
            { payments: ['guestPaymentRequest'] }
        ]
    })(reducer);
}

export const metaReducers: MetaReducer<AppState>[] = !environment.production
    ? [debug, localStorageSyncReducer]
    : [localStorageSyncReducer];

export interface RouterStateUrl {
    url: string;
    params: Params;
    queryParams: Params;
    root: ActivatedRouteSnapshot;
}

/**
 * the router state snapshot is a mutable object,
 * which can cause issues when developing with store freeze to prevent direct state mutations.
 * This can be avoided by using a custom serializer.
 */
@Injectable()
export class CustomSerializer implements RouterStateSerializer<RouterStateUrl> {
    serialize(routerState: RouterStateSnapshot): RouterStateUrl {
        let route = routerState.root;
        let componentTree: (Type<any> | string | null)[] = [];
        while (route.firstChild) {
            route = route.firstChild;
            componentTree = [...componentTree, route.component];
        }

        const { url } = routerState;
        const queryParams = routerState.root.queryParams;
        const params = route.params;

        // Only return an object including the URL, params and query params
        // instead of the entire snapshot
        const root = new ActivatedRouteSnapshot();
        root.component = route.component;
        root.data = { componentTree };
        return {
            url,
            params,
            queryParams,
            root
        };
    }
}

export const reducers: ActionReducerMap<AppState> = {
    cart: fromCart.CartReducer,
    layout: layout.ComponentsReducer,
    samplingSupplies: samplingSupplies.SamplingSuppliesReducer,
    contact: fromContact.ContactReducer,
    auth: fromAuth.AuthReducer,
    payments: fromPayments.reducer,
    router: routerReducer
};

/**
 * The createFeatureSelector function selects a piece of state from the root of the state object.
 * This is useful for selecting feature states that are loaded eagerly or lazily.
 */
export const getSamplingSuppliesState = createFeatureSelector<samplingSupplies.State>('samplingSupplies');

export const getCatalogueItems = createSelector(
    getSamplingSuppliesState,
    samplingSupplies.getCatalogueItems
);

export const getCatalogueItemEntities = createSelector(
    getSamplingSuppliesState,
    samplingSupplies.getCatalogueItemEntities
);

export const getLoadedCategoryIds = createSelector(
    getSamplingSuppliesState,
    samplingSupplies.getLoadedCategoryIds
);

export const getLoadingCategoryIds = createSelector(
    getSamplingSuppliesState,
    samplingSupplies.getLoadingCategoryIds
);

export const getCategories = createSelector(
    getSamplingSuppliesState,
    samplingSupplies.getCategories
);

export const getCategoryToCatalogueItemsMap = createSelector(
    getSamplingSuppliesState,
    samplingSupplies.getCategoryToCatalogueItemsMap
);

export const getSamplingSuppliesLoading = createSelector(
    getSamplingSuppliesState,
    samplingSupplies.getSamplingSuppliesLoading
);

export const getCartState = createFeatureSelector<fromCart.State>('cart');

export const getSamplingSuppliesGroupId = createSelector(
    getCartState,
    fromCart.getSamplingSuppliesGroupId
);

export const getSamplingSuppliesSampling = createSelector(
    getCartState,
    fromCart.getSamplingSuppliesSampling
);

export const getCartId = createSelector(
    getCartState,
    fromCart.getCartId
);

export const getCartForCheckout = createSelector(
    getCartState,
    fromCart.getCartForCheckout
);

export const getCheckoutError = createSelector(
    getCartState,
    fromCart.getCheckoutError
);

export const getCheckoutVoucherError = createSelector(
    getCartState,
    fromCart.getCheckoutVoucherError
);

export const isCartPriceZero = createSelector(
    getCartState,
    fromCart.isCartPriceZero
);

export const isCartLoading = createSelector(
    getCartState,
    fromCart.isCartLoading
);

export const isPlacingOder = createSelector(
    getCartState,
    fromCart.isPlacingOder
);

export const cartWithSamplingSupply = createSelector(
    getCartState,
    fromCart.cartWithSamplingSupply
);

export const getCartPrice = createSelector(
    getCartState,
    fromCart.getCartPrice
);

export const getCartGroups = createSelector(
    getCartState,
    fromCart.getCartGroups
);

export const getOrderStatus = createSelector(
    getCartState,
    fromCart.getOrderStatus
);

export const getIsCartEmpty = createSelector(
    getCartState,
    fromCart.getIsCartEmpty
);
export const getCartUpdated = createSelector(
    getCartState,
    fromCart.getCartUpdated
);

export const isCartItemUnavailable = createSelector(
    getCartState,
    fromCart.isCartItemUnavailable
);

export const getCartServiceItems = createSelector(
    getCartState,
    fromCart.getCartServiceItems
);

export const getCartSamplingSupplyItems = createSelector(
    getCartState,
    fromCart.getCartSamplingSupplyItems
);

export const getCartRecommendedSupplies = createSelector(
    getCartState,
    fromCart.getCartRecommendedSupplies
);

export const getCartParams = createSelector(
    getCartState,
    fromCart.getCartParams
);

export const getIsAddingAnalyticalTest = createSelector(
    getCartState,
    fromCart.getIsAddingAnalyticalTest
);

export const getCartUpdateError = createSelector(
    getCartState,
    fromCart.getCartUpdateError
);

export const getLayoutState = createFeatureSelector<fromLayout.State>('layout');

export const getLayoutSamplingSupplies = createSelector(
    getLayoutState,
    layout.getLayoutSamplingSupplies
);

export const getSampleTypeFinderPopup = createSelector(
    getLayoutState,
    fromLayout.getSampleTypeSelectorPopup
);

export const getSamplingSuppliesQuantityError = createSelector(
    getLayoutState,
    fromLayout.getSamplingSuppliesQuantityError
);

export const getAuthState = createFeatureSelector<fromAuth.State>('auth');
export const getAuthToken = createSelector(
    getAuthState,
    fromAuth.getAuthToken
);
export const getLoggedIn = createSelector(
    getAuthState,
    fromAuth.getLoggedIn
);
export const getLoggedOut = createSelector(
    getAuthState,
    fromAuth.getLoggedOut
);

export const getProfile = createSelector(
    getAuthState,
    fromAuth.getProfile
);

export const getLoginError = createSelector(
    getAuthState,
    fromAuth.getLoginError
);

export const getContactState = createFeatureSelector<fromContact.State>('contact');

export const getDeliveryAddressSuggestions = createSelector(
    getContactState,
    fromContact.deliveryAddressSuggestions
);

export const getLoadingContact = createSelector(
    getContactState,
    fromContact.getLoadingContact
);

export const getLoggedInContact = createSelector(
    getContactState,
    fromContact.getLoggedInContact
);
export const getLoggedInContactId = createSelector(
    getContactState,
    fromContact.getLoggedInContactId
);
export const getIsContactChecked = createSelector(
    getContactState,
    fromContact.getIsContactChecked
);


export const getLoggedInContactHasOrganization = createSelector(
    getContactState,
    fromContact.getLoggedInContactHasOrganization
);

/* export const getOrderCheckoutState = createFeatureSelector<fromOrderCheckout.State>('orderCheckout');

export const getOrderCheckoutInformation = createSelector(
  getOrderCheckoutState,
  fromOrderCheckout.getOrderCheckoutInformation
);


export const getSameInvoiceAddressSwitch = createSelector(
  getOrderCheckoutState,
  fromOrderCheckout.getSameInvoiceAddressSwitch
);*/

export const getIsAddingItemToCartFn = (itemId) => createSelector(
    getCartState,
    fromCart.isAddingItemToCartFn(itemId)
);
export const getIsAddingItemToCart = createSelector(
    getCartState,
    fromCart.isAddingItemToCart
);

export const getAutoPopulatedCartItems = createSelector(
    getCartState,
    fromCart.getAutoPopulatedCartItems
);

export const getNonAutoPopulatedCartItems = createSelector(
    getCartState,
    fromCart.getNonAutoPopulatedCartItems
);

export const getStatusMessage = createSelector(
    getLayoutState,
    fromLayout.getStatusMessage
);


// Action
export class NoopAction implements Action {
    readonly type = 'No operation action';
}
